// keywordAutocomplete/suggestionMenu.js — a small vanilla dropdown for the contenteditable keyword autocomplete.
//
// The editor keeps the focus (not the menu), so the controller forwards keyboard (↑/↓/Enter/Esc) via the API;
// the menu also supports mouse (hover highlights, click selects). Items use mousedown+preventDefault so clicking
// never blurs the editor. Hidden keywords get a distinct (accent) colour. Positioned at the caret rect — below
// it, or above it when there is no room below (see placeAt).
//
// Factory + createDisposers teardown. { element, show(items, anchorRect, anchorEl), hide, isOpen, highlightNext,
// highlightPrev, selectHighlighted, destroy }. The anchor rect is viewport-based ({ left, top, bottom }), which
// is what the menu needs: it is `position: fixed`, so no scroll offset enters the calculation.

import { createDisposers } from "../core/disposers.js";

const STYLE_ID = "topo2-ac-menu-style";
function ensureStyle() {
    if (document.getElementById(STYLE_ID)) { return; }
    // Die Darstellung der Liste lebt seit 2026-09-25 in stylesheet_vite/src/topo2/autocomplete.css — dort, wo
    // auch die Tokens stehen (die Farbe versteckter Schlagwörter ist einer). Hier bleibt nur ein
    // Rückfall-Minimum für den Fall, dass die topo2-Basis auf einer Seite NICHT geladen ist: ohne Position und
    // Hintergrund wäre die Liste unbrauchbar, alles Übrige ist Kosmetik.
    const s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent =
        ".topo2-ac-menu{position:fixed;z-index:2000;background:#fff}";
    document.head.appendChild(s);
}

export function createSuggestionMenu({ onSelect = () => {} } = {}) {
    ensureStyle();
    const disposers = createDisposers();
    const root = document.createElement("div");
    root.className = "topo2-ac-menu";
    root.hidden = true;
    document.body.appendChild(root);
    disposers.add(() => root.remove());

    let items = [];
    let highlighted = -1;
    let openSide = null;   // "below" | "above" — fixed for as long as the menu stays open, see placeAt

    // Oben oder unten? Die Liste hängt an der aktuellen Zeile und geht normalerweise darunter auf. Ist dort kein
    // Platz mehr UND oben mehr, klappt sie um. Reicht KEINE Seite, bleibt es bei unten — dann scrollt sie, statt
    // in den noch engeren Raum darüber zu springen. Dasselbe Verhalten hatte die Legacy-Fassung über jQuery UI
    // (`collision: 'flip'`, javascript/basic.js:1838); beim Nachbau war es nicht mitgekommen (User 2026-09-28).
    //
    // Die Seite wird EINMAL je Öffnung bestimmt und dann festgehalten (hide() setzt sie zurück). Sonst entschiede
    // jeder Tastendruck neu: die Höhe hängt an der Trefferzahl, und nahe am Fensterrand passt EIN Treffer unten
    // noch, acht nicht mehr — die Liste spränge beim Tippen zwischen oben und unten hin und her.
    //
    // Bezug ist das FENSTER, nicht das Popup: die Liste sitzt `position: fixed` am body und darf das Popup
    // verlassen. `anchor.top` ist die Oberkante der aktuellen ZEILE, nicht die des Feldes — bei mehrzeiliger
    // Eingabe soll die Liste direkt über der Zeile stehen, in der geschrieben wird.
    function placeAt(anchor) {
        const height = root.getBoundingClientRect().height;
        const anchorTop = anchor.top ?? anchor.bottom;
        if (openSide === null) {
            const below = window.innerHeight - anchor.bottom;
            const above = anchorTop;
            openSide = (height > below && above > below) ? "above" : "below";
        }
        const top = openSide === "above" ? Math.max(0, anchorTop - height) : anchor.bottom;
        root.style.left = Math.round(anchor.left) + "px";
        root.style.top = Math.round(top) + "px";
    }

    function render() {
        root.innerHTML = "";
        items.forEach((it, i) => {
            const el = document.createElement("div");
            el.className = "topo2-ac-item" + (it.hidden ? " is-hidden" : "") + (i === highlighted ? " is-highlighted" : "");
            el.textContent = it.label;
            el.title = it.label;   // a long keyword ends with "…" (max-width in topo2/autocomplete.css) — full text here
            // mousedown (not click) + preventDefault → selecting doesn't blur the editor.
            el.addEventListener("mousedown", (ev) => { ev.preventDefault(); onSelect(it); hide(); });
            el.addEventListener("mousemove", () => setHighlight(i));
            root.appendChild(el);
        });
    }

    function setHighlight(i) {
        highlighted = i;
        for (let k = 0; k < root.children.length; k++) { root.children[k].classList.toggle("is-highlighted", k === i); }
    }

    // Keyboard only: keep the highlighted item inside the visible part of the list (it scrolls — max-height in
    // topo2/autocomplete.css). Before, ↓ walked out of sight in a long list (User 2026-10-06). Not on mouse hover:
    // a half-visible item scrolled under the pointer would pass the highlight on and on. scrollTop, not
    // scrollIntoView — the list is position: fixed, and scrollIntoView may scroll the page as well.
    function revealHighlighted() {
        const el = root.children[highlighted];
        if (!el) { return; }
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (top < root.scrollTop) { root.scrollTop = top; }
        else if (bottom > root.scrollTop + root.clientHeight) { root.scrollTop = bottom - root.clientHeight; }
    }

    function show(next, anchorRect, anchorEl) {
        items = next || [];
        if (!items.length) { hide(); return; }
        highlighted = 0;
        render();
        root.hidden = false;
        root.scrollTop = 0;   // a new list starts at its first item, not where the last one was scrolled to
        // Schriftgröße vom Feld übernehmen, an dem die Liste hängt (User 2026-09-25). Sie lag als fester Wert
        // im Stylesheet und passte damit nur zufällig; die Liste sitzt als `position: fixed` am body und kann
        // sie nicht erben, also wird sie übertragen.
        if (anchorEl) {
            root.style.fontSize = window.getComputedStyle(anchorEl).fontSize;
        }
        fitWidthToScrollbar();   // after the font size — the text width depends on it
        // NACH dem Rendern und Sichtbarmachen und NACH der Schriftgröße — vorher hätte die Liste nicht ihre
        // endgültige Höhe, und genau die entscheidet über die Seite.
        if (anchorRect) { placeAt(anchorRect); }
    }

    // Firefox leaves the vertical scrollbar out of the list's shrink-to-fit width, so the widest entry is cut by
    // exactly the scrollbar's width. Widen the list by that much — only when the list scrolls AND an entry would be
    // cut; max-width in topo2/autocomplete.css still caps it (User 2026-10-07: a fixed scrollbar-gutter left short
    // lists with an empty strip on the right).
    function fitWidthToScrollbar() {
        root.style.width = '';
        const scrollbar = root.offsetWidth - root.clientWidth - 2 * root.clientLeft;   // clientLeft = border width
        if (scrollbar <= 0) { return; }
        const cut = [...root.children].some((el) => el.scrollWidth > el.clientWidth);
        if (cut) { root.style.width = (parseFloat(window.getComputedStyle(root).width) + scrollbar) + 'px'; }
    }
    function hide() { root.hidden = true; items = []; highlighted = -1; openSide = null; }
    function isOpen() { return !root.hidden; }
    function highlightNext() { if (items.length) { setHighlight((highlighted + 1) % items.length); revealHighlighted(); } }
    function highlightPrev() { if (items.length) { setHighlight((highlighted - 1 + items.length) % items.length); revealHighlighted(); } }
    function selectHighlighted() {
        if (highlighted >= 0 && highlighted < items.length) { onSelect(items[highlighted]); hide(); return true; }
        return false;
    }

    return {
        element: root,
        show, hide, isOpen, highlightNext, highlightPrev, selectHighlighted,
        destroy: () => disposers.dispose(),
    };
}
